import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogClose,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { X, Rocket, Clock } from "lucide-react";
import { SidebarMenuButton } from "./ui/sidebar";
import { useUpdates } from "@/hooks/useUpdates";
import Discord from "./icons/discord";
import Github from "./icons/github";
import LinkedIn from "./icons/linkedin";
import Link from "next/link";
import {
  LIBUM_DISCORD_URL,
  LIBUM_GITHUB_URL,
  LIBUM_LINKEDIN_URL,
} from "@/lib/constants";
import { useIsMobile } from "@/hooks/useIsMobile";
import { formatLongDate } from "@/utils/utils";
import Image from "next/image";

export function UpdateDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const {
    update,
    updates,
    selectedUpdateIndex,
    hasUnreadUpdates,
    shouldAutoShow,
    markUpdatesAsRead,
    selectUpdate,
  } = useUpdates();
  const isMobile = useIsMobile();

  // Auto-open dialog when there's a new version
  useEffect(() => {
    if (shouldAutoShow) {
      setIsOpen(true);
      markUpdatesAsRead();
    }
  }, [shouldAutoShow, markUpdatesAsRead]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    // Mark updates as read when the dialog is opened
    if (open) {
      markUpdatesAsRead();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <SidebarMenuButton
          variant="custom"
          tooltip={{
            children: "What's New",
            hidden: false,
          }}
          className="relative flex size-8 !w-8 shrink-0 items-center !justify-center !gap-0"
        >
          <Rocket className="!size-5" />
        </SidebarMenuButton>
      </DialogTrigger>

      {hasUnreadUpdates && (
        <span
          className={`absolute ${isMobile ? "right-2 top-[0.25]" : "right-0 top-0"} z-50 h-1.5 w-1.5 rounded-full bg-red-500`}
        />
      )}

      <DialogContent
        className={`flex ${isMobile ? "max-h-[85vh] max-w-[95vw]" : "max-h-[90vh]"} flex-col bg-card`}
        onOpenAutoFocus={(e) => {
          if (shouldAutoShow) {
            e.preventDefault();
          }
        }}
      >
        <DialogHeader className="flex flex-row items-start justify-between">
          <div>
            <DialogTitle className="text-xl font-bold text-gray-1100 dark:text-gray-100">
              What&apos;s New
            </DialogTitle>
            <DialogDescription className="flex items-center gap-2">
              {formatLongDate(update.releaseDate.getTime() / 1000)}
              {selectedUpdateIndex !== 0 && (
                <span className="inline-flex items-center rounded border border-rose-400 px-1 py-0.5 text-[10px] font-medium leading-none text-rose-400">
                  Archived
                </span>
              )}
            </DialogDescription>
          </div>
          <DialogClose className="!mt-0 rounded-sm text-gray-1100 opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:opacity-100 focus:outline-brand-primary dark:text-gray-100">
            <X className="size-7" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>

        <div className="lb-scrollbar flex flex-col overflow-y-auto px-2">
          {update.image && (
            <div className="flex px-4 pb-6 pt-4">
              <Image
                src={update.image}
                alt={`What's new - ${update.id}`}
                height={1080}
                width={1920}
                style={{ height: "auto", width: "100%" }}
                className="rounded-lg"
                priority
              />
            </div>
          )}
          {/* Update main description */}
          <div>{update.opener}</div>
          {/* Update groups */}
          {update.groups.map((group, idx) => (
            <div
              key={group.id}
              className={`flex flex-col ${idx === update.groups.length - 1 ? "pb-4" : ""}`}
            >
              <div className="flex flex-grow items-center gap-2 py-4">
                <h3
                  className={`flex shrink-0 whitespace-nowrap font-headline text-xs font-semibold uppercase sm:text-base ${idx % 3 === 0 ? "text-emerald-400" : idx % 3 === 1 ? "text-brand-accent" : "text-rose-400"}`}
                >
                  {idx > 0 && "..."}
                  {group.title}
                  {(idx < update.groups.length - 1 || idx === 0) && "..."}
                </h3>
                <div
                  className={`flex h-[1.5px] flex-grow ${idx % 3 === 0 ? "bg-emerald-400" : idx % 3 === 1 ? "bg-brand-accent" : "bg-rose-400"}`}
                ></div>
              </div>

              {/* Details within group */}
              {group.details.map((detail) => (
                <div
                  key={`${group.id}-${detail.id}`}
                  className="mb-2 flex items-start text-sm sm:text-base"
                >
                  <span className="mr-2 mt-[8px] size-[5px] min-h-[5px] min-w-[5px] rounded-full bg-muted sm:mt-[10px] sm:size-[6px] sm:min-h-[6px] sm:min-w-[6px]"></span>
                  {detail.node}
                </div>
              ))}
            </div>
          ))}
          {/* Update closing message */}
          <div>{update.closer}</div>
        </div>

        <DialogFooter>
          <div className="flex w-full items-center justify-between gap-2 border-t pt-4 text-xs text-muted">
            <div className="flex items-center gap-2">
              <div className="mt-0.5 flex gap-2">
                <Link href={LIBUM_DISCORD_URL} target="_blank">
                  <Discord className="size-4 fill-muted transition-colors duration-300 hover:fill-brand-accent" />
                  <span className="sr-only">Discord</span>
                </Link>
                <Link href={LIBUM_GITHUB_URL} target="_blank">
                  <Github className="size-4 fill-muted transition-colors duration-300 hover:fill-brand-accent" />
                  <span className="sr-only">Github</span>
                </Link>
                <Link href={LIBUM_LINKEDIN_URL} target="_blank">
                  <LinkedIn className="size-4 fill-muted transition-colors duration-300 hover:fill-brand-accent" />
                  <span className="sr-only">Linkedin</span>
                </Link>
              </div>
              <span className="flex">Follow us for more updates!</span>
            </div>
            {updates.length > 1 && (
              <TooltipProvider delayDuration={0}>
                <DropdownMenu
                  onOpenChange={(open) => !open && setTooltipOpen(false)}
                >
                  <Tooltip open={tooltipOpen}>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger
                        className="mt-0.5"
                        onMouseEnter={() => setTooltipOpen(true)}
                        onMouseLeave={() => setTooltipOpen(false)}
                      >
                        <Clock className="size-4 text-muted transition-colors duration-300 hover:text-brand-accent" />
                        <span className="sr-only">View historical updates</span>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <TooltipContent sideOffset={8}>
                      <p>History</p>
                    </TooltipContent>
                  </Tooltip>
                  <DropdownMenuContent
                    align="end"
                    className="max-h-[300px] min-w-0 overflow-y-auto"
                  >
                    {updates.map((updateItem, index) => (
                      <DropdownMenuItem
                        key={updateItem.id}
                        onClick={() => {
                          selectUpdate(index);
                          setTooltipOpen(false);
                        }}
                        className={`w-full cursor-pointer justify-center px-3 py-1 text-xs font-medium ${selectedUpdateIndex === index ? "bg-accent" : ""} ${index > 0 ? "mt-1" : ""}`}
                      >
                        {formatLongDate(
                          updateItem.releaseDate.getTime() / 1000,
                        )}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </TooltipProvider>
            )}
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
